import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Skeleton } from '@/components/ui/skeleton'
import { Search, Users } from 'lucide-react'
import { MainLayout } from '@/components/layout'
import { useDocumentTitle } from '@/hooks/use-document-title'
import { useAdminPatients, useUpdatePatientStatus } from '@/features/admin/hooks/use-admin-patients'
import { AdminPatientCard } from '@/features/admin/components/AdminPatientCard'
import { DeletePatientModal } from '@/features/admin/components/DeletePatientModal'
import { useToast } from '@/hooks/use-toast'
import { useQueryClient } from '@tanstack/react-query'

export const Route = createFileRoute('/_authenticated/admin/patients')({
  component: AdminPatientsPage,
})

function AdminPatientsPage() {
  useDocumentTitle('Gestão de Pacientes')
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; full_name: string } | null>(null)
  const { toast } = useToast()
  const queryClient = useQueryClient()

  const { data: patients, isLoading } = useAdminPatients()
  const updateStatusMutation = useUpdatePatientStatus()

  const handleChangeStatus = async (
    patientId: string,
    status: 'active' | 'inactive' | 'discharged',
  ) => {
    const actionLabel =
      status === 'active' ? 'ativar' : status === 'inactive' ? 'desativar' : 'dar alta'

    if (status === 'discharged') {
      if (!confirm('Tem certeza que deseja dar alta a este paciente? Esta ação pode ser revertida.')) {
        return
      }
    }

    try {
      await updateStatusMutation.mutateAsync({ patientId, status })
      toast({
        title: 'Status atualizado',
        description: `Paciente ${status === 'active' ? 'ativado' : status === 'inactive' ? 'desativado' : 'com alta'} com sucesso.`,
      })
    } catch {
      toast({
        title: 'Erro',
        description: `Não foi possível ${actionLabel} o paciente.`,
        variant: 'destructive',
      })
    }
  }

  // Filter patients
  const filteredPatients = patients?.filter((patient) => {
    const matchesSearch = patient.full_name.toLowerCase().includes(search.toLowerCase())
    const matchesStatus = statusFilter === 'all' || patient.status === statusFilter
    return matchesSearch && matchesStatus
  })

  // Group by status
  const activePatients = filteredPatients?.filter((p) => p.status === 'active') ?? []
  const inactivePatients = filteredPatients?.filter((p) => p.status === 'inactive') ?? []
  const dischargedPatients = filteredPatients?.filter((p) => p.status === 'discharged') ?? []

  return (
    <MainLayout title="Gestão de pacientes" subtitle="Visualize e gerencie todos os pacientes da organização">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <div className="p-2.5 bg-teal-50 rounded-lg">
            <Users className="h-5 w-5 text-teal-600" />
          </div>
          <div>
            <p className="text-sm text-gray-500">
              {patients?.length ?? 0} pacientes na organização
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="card p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                placeholder="Buscar pacientes..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-9 px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            >
              <option value="all">Todos os status</option>
              <option value="active">Ativos</option>
              <option value="inactive">Inativos</option>
              <option value="discharged">Alta</option>
            </select>
          </div>
        </div>

        {isLoading ? (
          <PatientsSkeleton />
        ) : filteredPatients && filteredPatients.length > 0 ? (
          <div className="space-y-6">
            {activePatients.length > 0 && (
              <div>
                <h2 className="text-lg font-medium text-gray-900 mb-4">
                  Ativos ({activePatients.length})
                </h2>
                <div className="grid gap-4 md:grid-cols-2">
                  {activePatients.map((patient) => (
                    <AdminPatientCard
                      key={patient.id}
                      patient={patient}
                      onChangeStatus={handleChangeStatus}
                      onDelete={setDeleteTarget}
                    />
                  ))}
                </div>
              </div>
            )}

            {inactivePatients.length > 0 && (
              <div>
                <h2 className="text-lg font-medium text-gray-500 mb-4">
                  Inativos ({inactivePatients.length})
                </h2>
                <div className="grid gap-4 md:grid-cols-2">
                  {inactivePatients.map((patient) => (
                    <AdminPatientCard
                      key={patient.id}
                      patient={patient}
                      onChangeStatus={handleChangeStatus}
                      onDelete={setDeleteTarget}
                    />
                  ))}
                </div>
              </div>
            )}

            {dischargedPatients.length > 0 && (
              <div>
                <h2 className="text-lg font-medium text-gray-500 mb-4">
                  Alta ({dischargedPatients.length})
                </h2>
                <div className="grid gap-4 md:grid-cols-2">
                  {dischargedPatients.map((patient) => (
                    <AdminPatientCard
                      key={patient.id}
                      patient={patient}
                      onChangeStatus={handleChangeStatus}
                      onDelete={setDeleteTarget}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="card p-12 text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-gray-100 rounded-full mb-4">
              <Users className="h-6 w-6 text-gray-400" />
            </div>
            <p className="text-gray-600 font-medium">Nenhum paciente encontrado</p>
            <p className="text-sm text-gray-500 mt-1">
              {search || statusFilter !== 'all'
                ? 'Tente ajustar os filtros'
                : 'Adicione pacientes atraves da pagina de pacientes'}
            </p>
          </div>
        )}

        <DeletePatientModal
          open={!!deleteTarget}
          onOpenChange={(open) => !open && setDeleteTarget(null)}
          patient={deleteTarget}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['admin-patients'] })
            queryClient.invalidateQueries({ queryKey: ['admin-stats'] })
            queryClient.invalidateQueries({ queryKey: ['patients'] })
          }}
        />
      </div>
    </MainLayout>
  )
}

function PatientsSkeleton() {
  return (
    <div className="grid gap-4 md:grid-cols-2">
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <div key={i} className="card p-4">
          <Skeleton className="h-32" />
        </div>
      ))}
    </div>
  )
}
